import React, { useEffect, useState } from "react";
import AppLayout from "@/components/layout/AppLayout";
import { Card } from "@/components/ui/card";
import { Table, TableHeader, TableHead, TableRow, TableBody, TableCell } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DataToolbar } from "@/components/data/Common";
import { api, formatErr, API } from "@/lib/api";
import { toast } from "sonner";

// Operator-entered fields (Production Data Model V1.0). Computed fields are display-only.
const groups = [
  ["Feedstock Handling", [
    ["feedstock_received", "Feedstock Received (MT)"],
    ["feedstock_curing", "Feedstock Curing (MT)"],
    ["feedstock_processed", "Feedstock Processed (MT)"],
  ]],
  ["Process & Recovery", [
    ["afr_produced", "AFR Produced (MT)"],
    ["ferrous_recovered", "Ferrous Recovered (MT)"],
    ["silt_inert_disposed", "Silt / Inert Disposed (MT)"],
    ["heavy_rejects_disposed", "Heavy Rejects Disposed (MT)"],
  ]],
  ["AFR Quality", [
    ["ncv_afr", "NCV - AFR (Kcal/kg)"],
    ["moisture_afr", "Moisture - AFR (%)"],
    ["ash_afr", "Ash - AFR (%)"],
  ]],
  ["Feedstock Quality", [
    ["feedstock_moisture", "Feedstock Moisture (%)"],
    ["feedstock_ash", "Feedstock Ash (%)"],
    ["feedstock_ncv", "Feedstock NCV (Kcal/kg)"],
  ]],
  ["Utilities", [
    ["fuel_consumption", "Fuel (Ltr)"],
    ["electricity_kwh", "Electricity (kWh)"],
  ]],
];
const fieldKeys = groups.flatMap(([, fs]) => fs.map(([k]) => k));
const blank = Object.fromEntries(fieldKeys.map((k) => [k, 0]));

// live preview of estimated moisture loss for the form
const moistureLoss = (f) =>
  (parseFloat(f.feedstock_processed) || 0) -
  ((parseFloat(f.afr_produced) || 0) + (parseFloat(f.ferrous_recovered) || 0) +
   (parseFloat(f.silt_inert_disposed) || 0) + (parseFloat(f.heavy_rejects_disposed) || 0));

export default function Production() {
  const [rows, setRows] = useState([]);
  const [projects, setProjects] = useState([]);
  const [pid, setPid] = useState("all");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({ project_id: "", date: today, shift: "A", remarks: "", ...blank });
  const [editOpen, setEditOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [editForm, setEditForm] = useState({ reason: "", remarks: "", ...blank });

  useEffect(() => { api.get("/projects").then((r) => { setProjects(r.data); if (r.data[0]) setForm((f) => ({ ...f, project_id: r.data[0].id })); }); }, []);
  const load = () => api.get("/production", { params: pid !== "all" ? { project_id: pid } : {} }).then((r) => setRows(r.data));
  useEffect(() => { load(); }, [pid]);

  const submit = async () => {
    if (form.date > today) return toast.error("Production date cannot be in the future");
    if (moistureLoss(form) < 0) return toast.error("Material balance error: AFR + Ferrous + Silt/Inert + Heavy Rejects exceed Feedstock Processed");
    try { await api.post("/production", form); toast.success("Production entry saved"); setOpen(false); load(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  const openEdit = (r) => {
    setEditRow(r);
    setEditForm({ reason: "", remarks: r.remarks || "", ...Object.fromEntries(fieldKeys.map((k) => [k, r[k] ?? 0])) });
    setEditOpen(true);
  };

  const submitEdit = async () => {
    if (!editForm.reason.trim()) return toast.error("Reason for revision is required");
    if (moistureLoss(editForm) < 0) return toast.error("Material balance error: outputs exceed Feedstock Processed");
    const payload = { reason: editForm.reason, remarks: editForm.remarks, ...Object.fromEntries(fieldKeys.map((k) => [k, editForm[k]])) };
    try { await api.patch(`/production/${editRow.id}`, payload); toast.success("Production entry revised"); setEditOpen(false); load(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  const filt = rows.filter((r) => !q || `${r.date} ${r.shift} ${r.remarks || ""}`.toLowerCase().includes(q.toLowerCase()));
  const num = (v, d = 2) => (v ?? 0).toLocaleString("en-IN", { minimumFractionDigits: d, maximumFractionDigits: d });

  const exportPDF = () => {
    const t = localStorage.getItem("neipl_token");
    const url = `${API}/reports/production/pdf${pid !== "all" ? `?project_id=${pid}` : ""}`;
    fetch(url, { headers: { Authorization: `Bearer ${t}` } })
      .then((r) => r.blob()).then((b) => { const u = URL.createObjectURL(b); window.open(u, "_blank"); });
  };

  const renderFormGroups = (state, setState) =>
    groups.map(([title, fs]) => (
      <div key={title} className="col-span-2 md:col-span-3">
        <div className="label-cap text-sky-800 font-semibold mb-1.5 mt-1">{title}</div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {fs.map(([k, lbl]) => (
            <div key={k}>
              <Label className="label-cap">{lbl}</Label>
              <Input type="number" step="0.01" data-testid={`production-${k}-input`}
                value={state[k]} onChange={(e) => setState({ ...state, [k]: parseFloat(e.target.value) || 0 })} />
            </div>
          ))}
        </div>
      </div>
    ));

  return (
    <AppLayout title="Production" subtitle="Daily / shift-wise production entries — Material balance & feedstock yard"
      actions={
        <Select value={pid} onValueChange={setPid}>
          <SelectTrigger className="w-48" data-testid="production-project-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All projects</SelectItem>
            {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
          </SelectContent>
        </Select>
      }
    >
      <DataToolbar
        search={q} setSearch={setQ} onAdd={() => setOpen(true)} addLabel="New Entry"
        onExport={exportPDF} addTestId="production-add-button" searchTestId="production-search-input"
      />
      <Card className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead><TableHead>Shift</TableHead>
              <TableHead className="text-right">Feedstock Received (MT)</TableHead>
              <TableHead className="text-right">Feedstock Processed (MT)</TableHead>
              <TableHead className="text-right">Closing Yard Stock (MT)</TableHead>
              <TableHead className="text-right">AFR Produced (MT)</TableHead>
              <TableHead className="text-right">Ferrous Recovered (MT)</TableHead>
              <TableHead className="text-right">Silt / Inert (MT)</TableHead>
              <TableHead className="text-right">Heavy Rejects (MT)</TableHead>
              <TableHead className="text-right">Est. Misc. Losses (MT)</TableHead>
              <TableHead className="text-right">NCV - AFR (Kcal/kg)</TableHead>
              <TableHead className="text-right">Moisture - AFR (%)</TableHead>
              <TableHead className="text-right">Ash - AFR (%)</TableHead>
              <TableHead className="text-right">Fuel (Ltr)</TableHead>
              <TableHead className="text-right">Electricity (kWh)</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filt.slice(0, 200).map((r) => (
              <TableRow key={r.id} data-testid={`production-row-${r.id}`}>
                <TableCell className="font-mono text-xs">{r.date}</TableCell>
                <TableCell>{r.shift}</TableCell>
                <TableCell className="text-right font-mono">{num(r.feedstock_received)}</TableCell>
                <TableCell className="text-right font-mono">{num(r.feedstock_processed)}</TableCell>
                <TableCell className="text-right font-mono text-slate-500">{num(r.closing_stock)}</TableCell>
                <TableCell className="text-right font-mono font-semibold text-sky-700">{num(r.afr_produced)}</TableCell>
                <TableCell className="text-right font-mono">{num(r.ferrous_recovered)}</TableCell>
                <TableCell className="text-right font-mono">{num(r.silt_inert_disposed)}</TableCell>
                <TableCell className="text-right font-mono">{num(r.heavy_rejects_disposed)}</TableCell>
                <TableCell className="text-right font-mono text-amber-700">{num(r.estimated_moisture_loss)}</TableCell>
                <TableCell className="text-right font-mono">{num(r.ncv_afr, 0)}</TableCell>
                <TableCell className="text-right font-mono">{num(r.moisture_afr, 1)}</TableCell>
                <TableCell className="text-right font-mono">{num(r.ash_afr, 1)}</TableCell>
                <TableCell className="text-right font-mono">{num(r.fuel_consumption, 1)}</TableCell>
                <TableCell className="text-right font-mono">{num(r.electricity_kwh, 0)}</TableCell>
                <TableCell className="text-right">
                  <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => openEdit(r)} data-testid={`production-edit-${r.id}`}>Edit / Revise</Button>
                </TableCell>
              </TableRow>
            ))}
            {!filt.length && <TableRow><TableCell colSpan={16} className="text-center py-12 text-slate-400">No production data</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="production-create-dialog">
          <DialogHeader><DialogTitle>New Production Entry</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div className="col-span-2 md:col-span-3 grid grid-cols-3 gap-3">
              <div>
                <Label className="label-cap">Project</Label>
                <Select value={form.project_id} onValueChange={(v) => setForm({ ...form, project_id: v })}>
                  <SelectTrigger data-testid="production-project-input"><SelectValue /></SelectTrigger>
                  <SelectContent>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="label-cap">Date</Label><Input type="date" max={today} data-testid="production-date-input" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
              <div>
                <Label className="label-cap">Shift</Label>
                <Select value={form.shift} onValueChange={(v) => setForm({ ...form, shift: v })}>
                  <SelectTrigger data-testid="production-shift-input"><SelectValue /></SelectTrigger>
                  <SelectContent>{["A", "B", "C"].map((s) => <SelectItem key={s} value={s}>Shift {s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            {renderFormGroups(form, setForm)}
            <div className="col-span-2 md:col-span-3 flex items-center justify-between rounded-md bg-slate-50 border px-3 py-2">
              <span className="label-cap text-slate-600">Estimated Miscellaneous Losses (auto)</span>
              <span className={`font-mono font-semibold ${moistureLoss(form) < 0 ? "text-red-600" : "text-slate-800"}`} data-testid="production-moisture-loss-preview">{moistureLoss(form).toFixed(2)} MT</span>
            </div>
            <div className="col-span-2 md:col-span-3"><Label className="label-cap">Remarks</Label>
              <Textarea data-testid="production-remarks-input" value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} />
            </div>
          </div>
          <DialogFooter><Button onClick={submit} data-testid="production-submit-button" className="bg-sky-900 hover:bg-sky-950">Save entry</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="production-edit-dialog">
          <DialogHeader><DialogTitle>Edit / Revise Production — {editRow?.date} · Shift {editRow?.shift}</DialogTitle></DialogHeader>
          <div className="text-xs text-slate-500 mb-1">Project, date and shift are locked. Yard balance & moisture loss recompute automatically. Every revision is recorded in the audit trail.</div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {renderFormGroups(editForm, setEditForm)}
            <div className="col-span-2 md:col-span-3 flex items-center justify-between rounded-md bg-slate-50 border px-3 py-2">
              <span className="label-cap text-slate-600">Estimated Miscellaneous Losses (auto)</span>
              <span className={`font-mono font-semibold ${moistureLoss(editForm) < 0 ? "text-red-600" : "text-slate-800"}`} data-testid="production-edit-moisture-loss-preview">{moistureLoss(editForm).toFixed(2)} MT</span>
            </div>
            <div className="col-span-2 md:col-span-3"><Label className="label-cap">Remarks</Label>
              <Textarea data-testid="production-edit-remarks-input" value={editForm.remarks} onChange={(e) => setEditForm({ ...editForm, remarks: e.target.value })} />
            </div>
            <div className="col-span-2 md:col-span-3"><Label className="label-cap">Reason for Revision *</Label>
              <Textarea data-testid="production-edit-reason-input" value={editForm.reason} onChange={(e) => setEditForm({ ...editForm, reason: e.target.value })} placeholder="Mandatory — explains why this entry is being corrected" />
            </div>
          </div>
          <DialogFooter><Button onClick={submitEdit} data-testid="production-edit-submit-button" className="bg-sky-900 hover:bg-sky-950">Save revision</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
